import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';

const AdminDashboardTabs = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('analytics');
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Use current pathname as base so tabs stay on /dashboard/weddings/:id
  const basePath = location.pathname;

  const tabs = [
    { id: 'analytics', label: 'Analytics', icon: 'BarChart3' },
    { id: 'guests', label: 'Guests', icon: 'Users' },
    { id: 'templates', label: 'Templates', icon: 'Layout' },
    { id: 'content', label: 'Content', icon: 'FileText' },
    { id: 'media', label: 'Media Library', icon: 'Image' },
    { id: 'settings', label: 'Settings', icon: 'Settings' },
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tabParam = searchParams?.get('tab');
    // Support legacy 'cms' parameter
    const normalizedTab = tabParam === 'cms' ? 'content' : tabParam;
    if (normalizedTab && tabs?.some(tab => tab?.id === normalizedTab)) {
      setActiveTab(normalizedTab);
    } else {
      setActiveTab('analytics');
    }
  }, [location?.search]);

  const handleTabClick = (tab) => {
    setActiveTab(tab?.id);
    navigate(`${basePath}?tab=${tab?.id}`);
    setIsMobileMenuOpen(false);
  };

  return (
    <>
      <div className="hidden md:block sticky top-0 z-100 bg-card border-b border-border shadow-romantic-sm">
        <div className="max-w-screen-xl mx-auto px-6">
          <nav role="navigation" aria-label="Admin dashboard navigation">
            <ul className="flex items-center gap-1">
              {tabs?.map((tab) => (
                <li key={tab?.id}>
                  <button
                    onClick={() => handleTabClick(tab)}
                    className={`
                      flex items-center gap-2 px-6 py-4
                      font-caption font-medium text-sm
                      transition-romantic border-b-2
                      ${activeTab === tab?.id
                        ? 'text-primary border-primary' :'text-muted-foreground border-transparent hover:text-foreground hover:border-muted'
                      }
                    `}
                    aria-current={activeTab === tab?.id ? 'page' : undefined}
                  >
                    <Icon 
                      name={tab?.icon} 
                      size={20} 
                      strokeWidth={activeTab === tab?.id ? 2.5 : 2}
                    />
                    <span>{tab?.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </div>
      <div className="md:hidden sticky top-0 z-100 bg-card border-b border-border shadow-romantic-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2">
            <Icon 
              name={tabs?.find(t => t?.id === activeTab)?.icon || 'Users'} 
              size={20} 
              color="var(--color-primary)"
            />
            <span className="font-caption font-medium text-foreground">
              {tabs?.find(t => t?.id === activeTab)?.label || 'Users'}
            </span>
          </div>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-muted transition-romantic press-scale"
            aria-label="Toggle menu"
            aria-expanded={isMobileMenuOpen}
          >
            <Icon 
              name={isMobileMenuOpen ? 'X' : 'Menu'} 
              size={24} 
              color="var(--color-foreground)"
            />
          </button>
        </div>

        {isMobileMenuOpen && (
          <nav 
            className="border-t border-border bg-background"
            role="navigation"
            aria-label="Admin dashboard mobile navigation"
          >
            <ul className="py-2">
              {tabs?.map((tab) => (
                <li key={tab?.id}>
                  <button
                    onClick={() => handleTabClick(tab)}
                    className={`
                      w-full flex items-center gap-3 px-4 py-3
                      font-caption font-medium text-sm
                      transition-romantic
                      ${activeTab === tab?.id
                        ? 'text-primary bg-primary/10' :'text-muted-foreground hover:text-foreground hover:bg-muted'
                      }
                    `}
                    aria-current={activeTab === tab?.id ? 'page' : undefined}
                  >
                    <Icon 
                      name={tab?.icon} 
                      size={20} 
                      strokeWidth={activeTab === tab?.id ? 2.5 : 2}
                    />
                    <span>{tab?.label}</span>
                  </button>
                </li>
              ))}
            </ul>
          </nav>
        )}
      </div>
    </>
  );
};

export default AdminDashboardTabs;